import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  api,
  type AdminContainerDetail,
  type SandboxProvider,
  type SandboxProviderSwitch,
} from "@/lib/api";
import { relativeIso } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRightLeft } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const PROVIDER_LABEL: Record<SandboxProvider, string> = { e2b: "E2B", vercel: "Vercel" };

const today = () => new Date().toISOString().slice(0, 10);

// The text the brain shows Boo after the switch. Moving to Vercel starts from
// empty sandboxes; moving back restores the E2B copies from before the move.
function defaultNotice(target: SandboxProvider, movedToVercelAt: string | null): string {
  if (target === "vercel") {
    return `Your sandbox was reset on ${today()}. Files and tools from before then are gone; recreate what you need.`;
  }
  const since = movedToVercelAt ? movedToVercelAt.slice(0, 10) : null;
  return since
    ? `Your sandbox was restored on ${today()} to how it was on ${since}. Files and tools created since ${since} are gone; recreate what you need.`
    : `Your sandbox was restored on ${today()} to an earlier copy. Recent files and tools may be gone; recreate what you need.`;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-start py-1.5">
      <div className="text-xs text-muted-foreground pt-0.5">{label}</div>
      <div className="min-w-0 text-sm break-all">{children}</div>
    </div>
  );
}

// A missing provider (a backend older than the column) reads as e2b, the default.
export function ProviderBadge({ provider = "e2b" }: { provider?: string }) {
  return provider === "vercel" ? (
    <Badge className="text-[10px]">Vercel</Badge>
  ) : (
    <Badge variant="outline" className="text-[10px] text-muted-foreground">
      {PROVIDER_LABEL[provider as SandboxProvider] ?? provider}
    </Badge>
  );
}

// Body of the "Sandbox provider" card: where this brain runs workspace
// sandboxes, and the switch between E2B and Vercel. A switch rewrites the
// brain's Railway env, which redeploys it.
export function SandboxProviderSection({ container }: { container: AdminContainerDetail }) {
  const queryClient = useQueryClient();
  const containerId = container.id;

  const { data: status, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-sandbox-provider", containerId],
    queryFn: () => api.admin.sandboxProvider(containerId),
  });

  const provider: SandboxProvider = status?.sandboxProvider ?? container.sandboxProvider;
  const live = status?.live ?? null;
  // What the brain actually runs on, when its env could be read. The offered
  // move is relative to that, so a record that drifted from the env still gets
  // the right action and confirm copy.
  const running: SandboxProvider | null =
    live?.provider === "e2b" || live?.provider === "vercel" ? live.provider : null;
  const drift = live !== null && live.provider !== provider;
  const target: SandboxProvider = (running ?? provider) === "vercel" ? "e2b" : "vercel";
  const changedAt = status?.sandboxProviderChangedAt ?? container.sandboxProviderChangedAt;
  const changedBy = status?.sandboxProviderChangedBy ?? container.sandboxProviderChangedBy;
  const vercelProjectId = status?.vercelProjectId ?? container.vercelProjectId;
  const orgProjectId = status?.orgVercelProjectId ?? null;
  const automatic = status?.vercelAutomatic ?? false;

  // Keyed on the target so the prefilled notice matches the direction offered.
  const [noticeFor, setNoticeFor] = useState<{ target: SandboxProvider; text: string } | null>(null);
  const notice =
    noticeFor?.target === target
      ? noticeFor.text
      : defaultNotice(target, target === "e2b" ? changedAt : null);
  const [token, setToken] = useState("");
  const [teamId, setTeamId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const manual = target === "vercel" && !automatic;
  const effectiveProjectId = projectId.trim() || vercelProjectId || "";
  const manualReady = !manual || (token.trim() !== "" && teamId.trim() !== "" && effectiveProjectId !== "");
  const hasService = container.railwayServiceId !== null;

  const switchProvider = useMutation({
    mutationFn: () => {
      const body: SandboxProviderSwitch = { provider: target, resetNotice: notice.trim() || undefined };
      if (manual) body.vercel = { token: token.trim(), teamId: teamId.trim(), projectId: effectiveProjectId };
      return api.admin.switchSandboxProvider(containerId, body);
    },
    onSuccess: (result) => {
      toast.success(`Moved to ${PROVIDER_LABEL[result.sandboxProvider]} — Boo is redeploying`);
      for (const w of result.warnings) toast.warning(w, { duration: 15_000 });
      setConfirmOpen(false);
      setToken("");
      setTeamId("");
      setProjectId("");
      setNoticeFor(null);
      for (const key of [
        ["admin-sandbox-provider", containerId],
        ["admin-container", containerId],
        ["admin-sandboxes-page", containerId],
        ["admin-vercel-sandboxes", containerId],
        ["admin-infrastructure"],
      ]) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Sandbox provider switch failed"),
  });

  if (isLoading) {
    return (
      <div className="space-y-2 py-1.5">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-64" />
      </div>
    );
  }

  // Without the status the card can't know automatic mode or the brain's env,
  // so it offers no switch rather than guessing. A failed background refetch
  // keeps the last good status on screen.
  if (isError && !status) {
    return (
      <div className="space-y-2 py-1.5">
        <p className="text-sm text-destructive break-words">
          Couldn't load the sandbox provider: {error instanceof Error ? error.message : "unknown error"}
        </p>
        <Button variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Row label="Provider">
        <ProviderBadge provider={provider} />
      </Row>
      <Row label="Vercel project">
        {vercelProjectId ? (
          <span className="font-mono text-xs">{vercelProjectId}</span>
        ) : (
          <span className="text-muted-foreground italic">—</span>
        )}
      </Row>
      <Row label="Last switched">
        {changedAt ? (
          <>
            {relativeIso(changedAt)}
            {changedBy && <span className="text-muted-foreground"> by {changedBy}</span>}
          </>
        ) : (
          <span className="text-muted-foreground italic">never</span>
        )}
      </Row>
      <Row label="Railway env">
        {live ? (
          <span className="font-mono text-xs">BOO_WORKSPACE_SANDBOX_PROVIDER={live.provider ?? "(unset)"}</span>
        ) : (
          <span className="text-muted-foreground italic">{hasService ? "couldn't read" : "no Railway service"}</span>
        )}
        {drift && (
          <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-500">
            <AlertTriangle className="size-3.5 shrink-0 mt-0.5" />
            {running
              ? `The brain runs on ${PROVIDER_LABEL[running]}, but this record says ${PROVIDER_LABEL[provider]}. The move below starts from what the brain runs, and records the result.`
              : live?.provider
                ? `The brain's env sets the provider to "${live.provider}", but this record says ${PROVIDER_LABEL[provider]}. The move below sets it explicitly.`
                : `The brain's env doesn't set a provider, but this record says ${PROVIDER_LABEL[provider]}. The move below sets it explicitly.`}
          </p>
        )}
        {live?.resetNotice && (
          <p className="mt-1 text-xs text-muted-foreground break-words">Reset notice: “{live.resetNotice}”</p>
        )}
      </Row>

      <div className="mt-2 border-t pt-3 pb-1 space-y-3">
        <div className="text-xs font-medium">
          {target === "vercel" ? "Move to Vercel Sandbox" : "Move back to E2B"}
        </div>

        {target === "vercel" &&
          (automatic ? (
            <p className="text-xs text-muted-foreground">
              {orgProjectId
                ? `Runs in the org's Vercel project ${orgProjectId}, shared by its Boos, and mints a project-scoped token for this Boo.`
                : "Creates the org's Vercel project, shared by its Boos, and mints a project-scoped token for this Boo."}
            </p>
          ) : (
            <div className="grid gap-2">
              <p className="text-xs text-muted-foreground">
                Paste a Vercel token scoped to the project this Boo should run in. It goes straight to
                the brain's Railway env and isn't stored here.
              </p>
              <div className="grid gap-1">
                <Label htmlFor="vercel-token" className="text-xs">Token</Label>
                <Input
                  id="vercel-token"
                  type="password"
                  autoComplete="off"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="vcp_…"
                  className="h-8 font-mono text-xs"
                />
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-1">
                  <Label htmlFor="vercel-team" className="text-xs">Team ID</Label>
                  <Input
                    id="vercel-team"
                    autoComplete="off"
                    value={teamId}
                    onChange={(e) => setTeamId(e.target.value)}
                    placeholder="team_…"
                    className="h-8 font-mono text-xs"
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="vercel-project" className="text-xs">Project ID</Label>
                  <Input
                    id="vercel-project"
                    autoComplete="off"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    placeholder={vercelProjectId ?? "prj_…"}
                    className="h-8 font-mono text-xs"
                  />
                </div>
              </div>
            </div>
          ))}

        <div className="grid gap-1">
          <Label htmlFor="reset-notice" className="text-xs">Reset notice</Label>
          <Textarea
            id="reset-notice"
            value={notice}
            onChange={(e) => setNoticeFor({ target, text: e.target.value })}
            maxLength={2000}
            className="text-xs"
          />
          <p className="text-[11px] text-muted-foreground">
            Shown to Boo after the switch. Leave empty to clear any earlier notice.
          </p>
        </div>

        <Button
          size="sm"
          variant={target === "vercel" ? "default" : "outline"}
          disabled={!hasService || !manualReady || switchProvider.isPending}
          onClick={() => setConfirmOpen(true)}
          title={hasService ? undefined : "This Boo has no Railway service to update"}
        >
          <ArrowRightLeft className="size-3.5 mr-1.5" />
          {target === "vercel" ? "Move to Vercel" : "Move back to E2B"}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={target === "vercel" ? `Move ${container.name} to Vercel Sandbox?` : `Move ${container.name} back to E2B?`}
        description={
          target === "vercel"
            ? "Writes the Vercel credentials to the brain's Railway env and redeploys it: Boo restarts and in-flight work is interrupted. Sandboxes start empty on Vercel. The paused E2B sandboxes are kept, so moving back restores them."
            : "Sets the provider back to E2B, removes the Vercel credentials from the brain's Railway env, and redeploys it: Boo restarts and in-flight work is interrupted. Boo reconnects its paused E2B sandboxes with their files; anything created on Vercel is left behind."
        }
        confirmLabel={target === "vercel" ? "Move to Vercel" : "Move back to E2B"}
        variant="default"
        loading={switchProvider.isPending}
        onConfirm={() => switchProvider.mutate()}
      >
        <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
          {target === "vercel" && (
            <div>
              Project:{" "}
              <span className="font-mono">
                {manual ? effectiveProjectId : (orgProjectId ?? "new project for the org")}
              </span>
            </div>
          )}
          <div className="break-words">
            {notice.trim() ? <>Reset notice: “{notice.trim()}”</> : "No reset notice (any earlier one is removed)."}
          </div>
        </div>
      </ConfirmDialog>
    </div>
  );
}
